import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Download, Trash2, Mail, Play, Square, Gauge } from "lucide-react";
import api, { formatApiError, cachedGet, apiCache } from "@/lib/api";
import { savePdf } from "@/lib/savePdf";
import { shareOrMailtoPdf } from "@/lib/sharePdf";
import { toast } from "sonner";
import EmailRecipientDialog from "@/components/EmailRecipientDialog";
import FieldHelp from "@/components/FieldHelp";

const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const today = () => new Date().toISOString().slice(0, 10);

const emptyStart = (workers) => ({
  worker_id: workers[0]?.id || "",
  date: today(),
  start_time: nowHHMM(),
  start_odometer: "",
  notes: "",
});

const emptyEnd = () => ({ end_time: nowHHMM(), end_odometer: "", notes: "" });

export default function KilometresPage() {
  const [rows, setRows] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [startForm, setStartForm] = useState(emptyStart([]));
  const [endForm, setEndForm] = useState(emptyEnd());
  const [activeTrip, setActiveTrip] = useState(null);
  const upd = (setter) => (k) => (e) =>
    setter((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const load = async () => {
    try {
      await Promise.all([
        cachedGet("/kilometres", null, (data) => setRows(data)),
        cachedGet("/workers", null, (data) => setWorkers(data)),
      ]);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const inProgress = rows.filter((r) => r.trip_state === "in_progress");
  const complete = rows.filter((r) => r.trip_state !== "in_progress");
  const totalKm = complete.reduce((s, r) => s + (r.km || 0), 0);

  const startTrip = async () => {
    if (!startForm.worker_id) { toast.error("Pick a worker"); return; }
    if (startForm.start_odometer === "" || startForm.start_odometer === null) {
      toast.error("Enter the starting odometer reading"); return;
    }
    try {
      const r = await api.post("/kilometres", {
        worker_id: startForm.worker_id,
        date: startForm.date,
        start_time: startForm.start_time,
        start_odometer: parseFloat(startForm.start_odometer),
        notes: startForm.notes,
      });
      toast.success("Trip started — finish it when you arrive.");
      setStartOpen(false);
      setStartForm(emptyStart(workers));
      if (r.data?.id) setRows((prev) => [r.data, ...prev]);
      apiCache.invalidate("/kilometres");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEnd = (trip) => {
    setActiveTrip(trip);
    setEndForm({ ...emptyEnd(), end_odometer: "" });
    setEndOpen(true);
  };

  const finishTrip = async () => {
    if (endForm.end_odometer === "" || endForm.end_odometer === null) {
      toast.error("Enter the end odometer reading"); return;
    }
    const endOdo = parseFloat(endForm.end_odometer);
    if (endOdo < (activeTrip?.start_odometer ?? 0)) {
      toast.error("End odometer can't be less than start"); return;
    }
    try {
      const r = await api.patch(`/kilometres/${activeTrip.id}/end`, {
        end_time: endForm.end_time,
        end_odometer: endOdo,
        notes: endForm.notes || undefined,
      });
      const km = (endOdo - activeTrip.start_odometer).toFixed(1);
      toast.success(`Trip complete · ${km} km logged`);
      setEndOpen(false);
      setActiveTrip(null);
      if (r.data?.id) setRows((prev) => prev.map((p) => (p.id === r.data.id ? r.data : p)));
      apiCache.invalidate("/kilometres");
      apiCache.invalidate("/notifications");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteTrip = async (id) => {
    if (!window.confirm("Delete this trip?")) return;
    try {
      const resp = await api.delete(`/kilometres/${id}`);
      if (resp.data?.pending_approval) toast("Deletion sent to client for approval");
      else {
        toast.success("Trip deleted");
        setRows((prev) => prev.filter((p) => p.id !== id));
      }
      apiCache.invalidate("/kilometres");
      apiCache.invalidate("/notifications");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get("/kilometres/pdf", { responseType: "blob" });
      await savePdf(res.data, "kilometres.pdf");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5 max-w-6xl" data-testid="km-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight">Kilometres / Petrol</h1>
          <p className="text-muted-foreground">
            Two-tap odometer log. Start the trip when you set off, finish it when you arrive — total km auto-calculated for your tax report.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadPdf} className="rounded-full font-bold gap-2 min-h-[48px]" data-testid="km-pdf-btn"><Download className="w-4 h-4" /> Tax PDF</Button>
          <Button variant="outline" onClick={() => setEmailOpen(true)} className="rounded-full font-bold gap-2 min-h-[48px]" data-testid="km-email-btn"><Mail className="w-4 h-4" /> Email</Button>
          <Button onClick={() => { setStartForm(emptyStart(workers)); setStartOpen(true); }}
                  className="rounded-full font-bold gap-2 min-h-[48px]"
                  style={{ background: "hsl(var(--client))" }} data-testid="start-trip-btn">
            <Play className="w-4 h-4" /> Start trip
          </Button>
        </div>
      </header>

      <Card className="rounded-3xl p-5 border-2 grid sm:grid-cols-3 gap-3" data-testid="km-summary">
        <div><div className="text-xs uppercase font-bold tracking-wider text-muted-foreground">Total km</div><div className="font-display font-black text-3xl">{totalKm.toFixed(1)}</div></div>
        <div><div className="text-xs uppercase font-bold tracking-wider text-muted-foreground">ATO rate</div><div className="font-display font-black text-3xl">$0.88/km</div></div>
        <div><div className="text-xs uppercase font-bold tracking-wider text-muted-foreground">Estimated claim</div><div className="font-display font-black text-3xl text-client">${(totalKm * 0.88).toFixed(2)}</div></div>
      </Card>

      {inProgress.length > 0 && (
        <Card className="rounded-3xl border-2 p-4 bg-amber-500/5 border-amber-500/30 space-y-3" data-testid="in-progress-card">
          <div className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-amber-600" />
            <h3 className="font-display font-extrabold text-lg">Trip{inProgress.length === 1 ? "" : "s"} in progress</h3>
            <Badge variant="outline" className="border-amber-500/50 text-amber-700">
              {inProgress.length}
            </Badge>
          </div>
          <div className="space-y-2">
            {inProgress.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-3 p-3 bg-background rounded-2xl border" data-testid={`in-progress-${t.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-bold">{t.worker_name}</div>
                  <div className="text-xs text-muted-foreground">
                    Started {t.date} at {t.start_time} · Odo <span className="font-mono font-bold">{t.start_odometer}</span>
                  </div>
                  {t.notes && <div className="text-xs italic mt-1">{t.notes}</div>}
                </div>
                <Button onClick={() => openEnd(t)} className="rounded-full font-bold gap-2"
                        style={{ background: "hsl(var(--client))" }} data-testid={`finish-trip-${t.id}`}>
                  <Square className="w-3.5 h-3.5" /> Finish trip
                </Button>
                <Button size="icon" variant="ghost" className="rounded-full text-destructive"
                        onClick={() => deleteTrip(t.id)} data-testid={`del-km-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="rounded-3xl border-2 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider font-bold">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Worker</th>
                <th className="text-left p-3">Time</th>
                <th className="text-right p-3">Start Odo</th>
                <th className="text-right p-3">End Odo</th>
                <th className="text-right p-3">Total km</th>
                <th className="text-left p-3">Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {complete.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`km-row-${r.id}`}>
                  <td className="p-3 font-bold whitespace-nowrap">{r.date}</td>
                  <td className="p-3">{r.worker_name}</td>
                  <td className="p-3 text-xs whitespace-nowrap">{r.start_time || "—"} → {r.end_time || "—"}</td>
                  <td className="p-3 font-mono text-right">{r.start_odometer ?? "—"}</td>
                  <td className="p-3 font-mono text-right">{r.end_odometer ?? "—"}</td>
                  <td className="p-3 font-mono font-bold text-right">{(r.km || 0).toFixed(1)}</td>
                  <td className="p-3 text-xs max-w-[260px]">{r.notes || r.purpose || ""}</td>
                  <td className="p-3">
                    <Button size="sm" variant="ghost" className="rounded-full text-destructive"
                            onClick={() => deleteTrip(r.id)} data-testid={`del-km-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {complete.length === 0 && (
                <tr><td colSpan="8" className="p-8 text-center text-muted-foreground">No completed trips yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Start dialog */}
      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="km-start-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Play className="w-5 h-5" /> Start trip</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Record the starting odometer before you set off. You'll finish the trip after you arrive.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Worker</Label>
              <Select value={startForm.worker_id} onValueChange={(v) => setStartForm((f) => ({ ...f, worker_id: v }))}>
                <SelectTrigger className="rounded-xl min-h-[48px]" data-testid="km-worker"><SelectValue placeholder="Worker" /></SelectTrigger>
                <SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Date</Label>
              <Input type="date" value={startForm.date} onChange={upd(setStartForm)("date")} className="rounded-xl min-h-[48px]" data-testid="km-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Start time</Label>
              <Input type="time" value={startForm.start_time} onChange={upd(setStartForm)("start_time")} className="rounded-xl min-h-[48px]" data-testid="km-start-time" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider flex items-center">
                Start odometer
                <FieldHelp text="Read this off the dashboard before you start driving. Total km will be calculated from start - end at the end of the trip." learnMoreId="kilometres" />
              </Label>
              <Input type="number" inputMode="decimal" step="0.1" min="0" placeholder="e.g. 120450"
                     value={startForm.start_odometer} onChange={upd(setStartForm)("start_odometer")}
                     className="rounded-xl min-h-[48px] font-mono text-lg" data-testid="km-start-odo" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Brief notes (purpose, destination…)</Label>
              <Textarea rows={2} value={startForm.notes} onChange={upd(setStartForm)("notes")}
                        className="rounded-xl" data-testid="km-notes"
                        placeholder="e.g. Pick up from home, drive to GP appointment" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={startTrip} className="rounded-full font-bold gap-2"
                    style={{ background: "hsl(var(--client))" }} data-testid="km-start-save-btn">
              <Play className="w-4 h-4" /> Start trip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finish dialog */}
      <Dialog open={endOpen} onOpenChange={setEndOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="km-end-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Square className="w-5 h-5" /> Finish trip</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Enter the end odometer to complete the trip.</DialogDescription>
          </DialogHeader>
          {activeTrip && (
            <div className="space-y-3">
              <div className="rounded-2xl bg-muted/40 p-3 text-xs space-y-1" data-testid="km-end-summary">
                <div><b>{activeTrip.worker_name}</b> · {activeTrip.date}</div>
                <div>Started at <b>{activeTrip.start_time}</b>, odometer <b className="font-mono">{activeTrip.start_odometer}</b></div>
                {activeTrip.notes && <div className="italic">{activeTrip.notes}</div>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase font-bold tracking-wider">End time</Label>
                  <Input type="time" value={endForm.end_time} onChange={upd(setEndForm)("end_time")} className="rounded-xl min-h-[48px]" data-testid="km-end-time" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase font-bold tracking-wider">End odometer</Label>
                  <Input type="number" inputMode="decimal" step="0.1" min={activeTrip.start_odometer ?? 0}
                         value={endForm.end_odometer} onChange={upd(setEndForm)("end_odometer")}
                         className="rounded-xl min-h-[48px] font-mono text-lg" data-testid="km-end-odo"
                         placeholder={String(((activeTrip.start_odometer || 0) + 1).toFixed(1))} />
                </div>
              </div>
              {endForm.end_odometer !== "" && parseFloat(endForm.end_odometer) >= (activeTrip.start_odometer || 0) && (
                <div className="rounded-2xl bg-client/10 border border-client/30 p-3 text-center" data-testid="km-end-preview">
                  <div className="text-xs uppercase font-bold tracking-wider text-muted-foreground">This trip</div>
                  <div className="font-display font-black text-3xl text-client">
                    {(parseFloat(endForm.end_odometer) - activeTrip.start_odometer).toFixed(1)} km
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-bold tracking-wider">Add to notes (optional)</Label>
                <Textarea rows={2} value={endForm.notes} onChange={upd(setEndForm)("notes")}
                          className="rounded-xl" placeholder="e.g. Returned via supermarket"
                          data-testid="km-end-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={finishTrip} className="rounded-full font-bold gap-2"
                    style={{ background: "hsl(var(--client))" }} data-testid="km-end-save-btn">
              <Square className="w-4 h-4" /> Finish trip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailRecipientDialog
        open={emailOpen} onOpenChange={setEmailOpen}
        title="Email kilometres tax report"
        description="Pick recipients from your address book. The latest kilometres PDF will be attached."
        onSend={async (recipients) => {
          const res = await api.get("/kilometres/pdf", { responseType: "blob" });
          const result = await shareOrMailtoPdf({
            blob: res.data,
            filename: "kilometres.pdf",
            recipients,
            subject: "Kilometres tax report",
            body: "Hi,\n\nPlease find the latest kilometres tax report attached.",
          });
          if (result === "shared") toast.success("Opened in your email app");
          else if (result === "mailto") toast.success(`PDF saved as “kilometres.pdf” in Downloads. Attach it in the email composer that just opened.`, { duration: 12000 });
        }}
      />
    </div>
  );
}
